import React, { useCallback, useEffect, useState } from 'react';
import { Category, LearningAsset, LearningBook, Tag } from '../../../../model/response';
import { TableParams } from '../../../../model/request';
import { Image, message, Popconfirm, Select, Table, TableProps } from 'antd';
import { deleteLearningAsset, getPageLearningAsset } from '../../../../service/learningAssetService';
import { DeleteOutlined, DownloadOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import Search from 'antd/es/input/Search';
import { findAllTags } from '../../../../service/tagService';
import { findAllCategories } from '../../../../service/categoryService';
import { useNavigate } from 'react-router-dom';
import { findAllLearningBooks } from '../../../../service/learningBookService';
import processError from '../../../../utils/processError';

function LearningAssetManagement() {
  const [fetching, setFetching] = useState(false);
  const [learningAssets, setLearningAssets] = useState<LearningAsset[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [learningBooks, setLearningBooks] = useState<LearningBook[]>([]);
  const [selectedLearningBookIds, setSelectedLearningBookIds] = useState<string[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    findAllTags()
      .then((tags) => setTags(tags))
      .catch((e) => console.error(e));
    findAllCategories()
      .then((categories) => setCategories(categories))
      .catch((e) => console.error(e));
    findAllLearningBooks()
      .then((learningBooks) => setLearningBooks(learningBooks))
      .catch((e) => console.error(e));
  }, []);

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
      showSizeChanger: true,
      defaultPageSize: 10,
    },
  });

  const handleLearningAssetPage: TableProps['onChange'] = (pagination, filters) => {
    setTableParams({
      pagination,
      filters,
    });
  };

  const fetchData = useCallback(() => {
    setFetching(true);
    getPageLearningAsset(tableParams, keyword, selectedTagIds, selectedCategoryIds, selectedLearningBookIds)
      .then((learningAssetPage) => {
        setLearningAssets(learningAssetPage.content);
        setTableParams({
          ...tableParams,
          pagination: {
            ...tableParams.pagination,
            total: learningAssetPage.totalElements,
          },
        });
      })
      .catch((error) => console.error(error))
      .finally(() => setFetching(false));
  }, [keyword, tableParams, selectedTagIds, selectedCategoryIds, selectedLearningBookIds]);

  useEffect(() => {
    if (tableParams.pagination) {
      setTableParams({
        pagination: {
          ...tableParams.pagination,
          current: 1,
        },
        filters: tableParams.filters,
      });
    }
    fetchData();
  }, [tableParams.pagination?.pageSize, keyword, selectedTagIds, selectedCategoryIds, selectedLearningBookIds]);

  useEffect(() => {
    fetchData();
  }, [tableParams.pagination?.current]);

  const learningAssetTableColumns: TableProps<LearningAsset>['columns'] = [
    {
      title: 'STT',
      dataIndex: 'key',
      render: (_, learningAsset, index) => <div>{index + 1}</div>,
    },
    {
      title: 'Tên học liệu số',
      key: 'name',
      render: (_, learningAsset) => <div className={'font-bold text-0B7BFF'}>{learningAsset.name}</div>,
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Thumbnail',
      key: 'thumbnailPath',
      align: 'center',
      render: (_, learningAsset) => (
        <div className={'flex items-center justify-center w-full'}>
          {learningAsset.thumbnailPath ? (
            <Image
              src={learningAsset.thumbnailPath}
              alt={learningAsset.name}
              className={'rounded-[2px]'}
              width={'auto'}
              height={60}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: 'Tải xuống',
      key: 'description',
      align: 'center',
      render: (_, learningAsset) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={() => handleDownloadLearningAsset(learningAsset)}>
            <DownloadOutlined />
          </button>
        </div>
      ),
    },
    {
      title: 'Hành động',
      key: 'doTest',
      align: 'center',
      render: (_, learningAsset) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={() => handleNavigateUpdateLearningAsset(learningAsset)}>
            <EditOutlined className="" />
          </button>
          <Popconfirm
            title="Xóa tài liệu số"
            description="Xác nhận xóa tài liệu số. Hành động không thể hoàn tác?"
            onConfirm={() => handleDeleteLearningAsset(learningAsset)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-red-500">
              <DeleteOutlined className={'text-white'} />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleDeleteLearningAsset = async (learningAsset: LearningAsset) => {
    try {
      await deleteLearningAsset(learningAsset.id);
      fetchData();
      message.success('Xoá tài liệu số thành công');
    } catch (err) {
      processError(err);
    }
  };

  function handleDownloadLearningAsset(learningAsset: LearningAsset) {
    const newWindow = window.open(learningAsset.filePath, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  }

  function handleNavigateUpdateLearningAsset(learningAsset: LearningAsset) {
    navigate(`/learning-assets/update/${learningAsset.id}`);
  }

  function handleNavigateAddLearningAsset() {
    navigate('/learning-assets/create');
  }

  return (
    <div className={'w-full h-full p-4'}>
      <div className={'p-2 flex items-center justify-between bg-white rounded'}>
        <div className={'flex gap-4'}>
          <button
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
            onClick={handleNavigateAddLearningAsset}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
          <div className="min-w-[170px] flex items-center gap-2">
            <div>Sách</div>
            <Select
              mode="multiple"
              onChange={setSelectedLearningBookIds}
              className="w-[300px]"
              placeholder="Bộ lọc - Sách"
              style={{ flex: 1 }}
              value={selectedLearningBookIds}
              options={learningBooks.map((learningBook) => ({
                label: learningBook.name,
                value: learningBook.id,
              }))}
              filterOption={(input, option) => !!option?.label?.toLowerCase()?.includes(input.toLowerCase())}
            />
          </div>
          <div className="min-w-[170px] flex items-center gap-2">
            <div>Thẻ</div>
            <Select
              mode="multiple"
              onChange={setSelectedTagIds}
              className="w-[300px]"
              placeholder="Bộ lọc - Thẻ"
              style={{ flex: 1 }}
              value={selectedTagIds}
              options={tags.map((tag) => ({
                label: tag.name,
                value: tag.id,
              }))}
              filterOption={(input, option) => !!option?.label?.toLowerCase()?.includes(input.toLowerCase())}
            />
          </div>
          <div className="min-w-[170px] flex items-center gap-2">
            <div>Chủ đề</div>
            <Select
              mode="multiple"
              onChange={setSelectedCategoryIds}
              className="w-[300px]"
              placeholder="Bộ lọc - Chủ đề"
              style={{ flex: 1 }}
              value={selectedCategoryIds}
              options={categories.map((tag) => ({
                label: tag.name,
                value: tag.id,
              }))}
              filterOption={(input, option) => !!option?.label?.toLowerCase()?.includes(input.toLowerCase())}
            />
          </div>
        </div>
        <Search
          placeholder={'Nhập từ khóa tìm kiếm'}
          rootClassName={'h-9 rounded-3xl border border-solid border-f46055 flex items-center overflow-hidden w-fit'}
          onSearch={setKeyword}
        />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          dataSource={learningAssets}
          columns={learningAssetTableColumns}
          rowKey={(learningAsset) => learningAsset.id}
          onChange={handleLearningAssetPage}
          loading={fetching}
          pagination={tableParams.pagination}
        />
      </div>
    </div>
  );
}

export default LearningAssetManagement;
